import { useEffect } from 'react';

/**
 * Globally stop mouse-wheel scrolling from changing the value of a focused
 * `<input type="number">`. Native browsers treat a wheel event over a focused
 * number input as increment/decrement, which silently corrupts figures when a
 * user scrolls the page with the cursor still over a field they just edited.
 *
 * One document-level listener covers every number input in the app (150+),
 * present or future, with no per-input wiring. Call it once, high in the tree
 * (App).
 *
 * Behaviour:
 *  - Only acts when the number input is the ACTIVE (focused) element, so
 *    scrolling PAST a number input you're not editing still scrolls the page.
 *  - Blurs nothing and swallows nothing else — arrow keys, typing, and the
 *    field's own validation are untouched; only the wheel-to-change is killed.
 *  - Listener is non-passive so preventDefault() actually suppresses the step.
 *
 * Desktop-only in effect: touch devices don't fire wheel events, so this is a
 * no-op there.
 */
export function useDisableNumberInputScroll() {
  useEffect(() => {
    const onWheel = (e) => {
      const el = e.target;
      // Only a focused number input. document.activeElement === el ensures we
      // don't hijack page scrolling when the cursor merely passes over a field.
      if (
        el instanceof HTMLInputElement &&
        el.type === 'number' &&
        document.activeElement === el
      ) {
        // Suppress the browser's increment/decrement. The page won't scroll
        // while the cursor is over the focused field, but that's the expected
        // trade-off — the user is editing it. Moving the cursor off the field
        // (or clicking elsewhere) restores normal scrolling immediately.
        e.preventDefault();
      }
    };

    // Non-passive: required for preventDefault() to take effect on wheel.
    document.addEventListener('wheel', onWheel, { passive: false });
    return () => document.removeEventListener('wheel', onWheel);
  }, []);
}

export default useDisableNumberInputScroll;
